import React from 'react';
import Axios from 'axios';
import dayjs from 'dayjs';
import {
    Box,
    Typography,
    Button,
    Chip,
    IconButton,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useSignOut } from 'react-auth-kit';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import { OFFICE_EMAIL } from '../../utils/officeContact';
import Alert from '../../components/alert/Alert';
import Loading from '../../components/Loading';

// Build a Gmail thread URL. Uses `authuser` to target the office account
// when admins are signed into multiple Google accounts. `#all/<id>` (not
// `#inbox/<id>`) so archived threads still resolve.
const buildGmailUrl = (threadId) =>
    `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(OFFICE_EMAIL)}#all/${threadId}`;

const STATUS_CHIP = {
    pending: { label: 'Awaiting reply', color: 'info' },
    followed_up_waiting: { label: 'Follow-up sent', color: 'warning' },
    replied: { label: 'Replied', color: 'success' },
    expired: { label: 'Expired', color: 'default' },
    canceled: { label: 'Canceled', color: 'default' },
    failed: { label: 'Failed', color: 'error' },
};

const STATUS_FILTER_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Awaiting reply' },
    { value: 'followed_up_waiting', label: 'Follow-up sent' },
    { value: 'replied', label: 'Replied' },
    { value: 'expired', label: 'Expired' },
    { value: 'canceled', label: 'Canceled' },
    { value: 'failed', label: 'Failed' },
];

const FollowUpDashboard = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const signOut = useSignOut();
    const navigate = useNavigate();

    const [followups, setFollowups] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [cancelingId, setCancelingId] = React.useState(null);
    const [successOpen, setSuccessOpen] = React.useState(false);
    const [successMsg, setSuccessMsg] = React.useState('');
    const [errorOpen, setErrorOpen] = React.useState(false);
    const [errorMsg, setErrorMsg] = React.useState('');
    const [confirmRow, setConfirmRow] = React.useState(null);
    const [statusFilter, setStatusFilter] = React.useState('active');

    const handleAuthError = (error, fallback) => {
        if (error.response?.status === 401) {
            signOut();
            localStorage.removeItem('userName');
            localStorage.removeItem('who');
            navigate('/signin');
            return;
        }
        setErrorMsg(error.response?.data?.error || fallback);
        setErrorOpen(true);
    };

    const fetchFollowups = async (status = statusFilter) => {
        try {
            const res = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/email-followups/active`,
                {
                    headers: { Authorization: getAuthHeader() },
                    params: { status },
                }
            );
            setFollowups(res.data.followups || []);
        } catch (error) {
            setFollowups([]);
            handleAuthError(error, 'Error fetching follow-ups');
        }
    };

    React.useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchFollowups(statusFilter);
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const handleRefresh = async () => {
        setLoading(true);
        await fetchFollowups(statusFilter);
        setLoading(false);
    };

    const handleCancel = async (row) => {
        setCancelingId(row.id);
        try {
            await Axios.delete(
                `${process.env.REACT_APP_URL}/api/v1/email-followups/track/${row.id}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setSuccessMsg('Follow-up cancelled');
            setSuccessOpen(true);
            await fetchFollowups();
        } catch (error) {
            handleAuthError(error, 'Error cancelling follow-up');
        } finally {
            setCancelingId(null);
        }
    };

    const renderDeadline = (row) => {
        const formatted = dayjs(row.followup_deadline).format('MM/DD HH:mm');
        const isActive = row.status === 'pending' || row.status === 'followed_up_waiting';
        if (!isActive) {
            return <Typography variant="body2" color="text.secondary">{formatted}</Typography>;
        }
        let sub;
        if (row.days_remaining <= 0) {
            sub = <Typography variant="caption" sx={{ color: colors.redAccent[500], fontWeight: 600 }}>Overdue</Typography>;
        } else if (row.days_remaining === 1) {
            sub = <Typography variant="caption" sx={{ color: colors.orangeAccent[500], fontWeight: 600 }}>&lt;24h</Typography>;
        } else {
            sub = <Typography variant="caption" color="text.secondary">{row.days_remaining}d left</Typography>;
        }
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <span>{formatted}</span>
                {sub}
            </Box>
        );
    };

    const columns = React.useMemo(() => [
        {
            id: 'index',
            header: '#',
            cell: ({ row, table }) => {
                const { pageIndex, pageSize } = table.getState().pagination;
                return pageIndex * pageSize + row.index + 1;
            },
        },
        {
            accessorKey: 'student_name',
            header: 'Student',
            cell: ({ row }) => {
                const r = row.original;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <span>
                            {r.student_name}
                            {r.korean_name ? ` (${r.korean_name})` : ''}
                        </span>
                        {r.consultation_number != null && (
                            <Typography variant="caption" color="text.secondary">
                                #{r.consultation_number}
                            </Typography>
                        )}
                    </Box>
                );
            },
        },
        {
            accessorKey: 'recipient_email',
            header: 'Recipient',
            cell: ({ getValue }) => (
                <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getValue()}
                </Box>
            ),
        },
        {
            accessorKey: 'subject',
            header: 'Subject',
            cell: ({ getValue }) => (
                <Box sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getValue()}
                </Box>
            ),
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ getValue }) => {
                const v = getValue();
                const chip = STATUS_CHIP[v] || { label: v, color: 'default' };
                return <Chip label={chip.label} color={chip.color} size="small" />;
            },
        },
        {
            accessorKey: 'sent_at',
            header: 'Sent',
            cell: ({ getValue }) => dayjs(getValue()).format('MM/DD HH:mm'),
        },
        {
            id: 'deadline',
            header: 'Deadline',
            cell: ({ row }) => renderDeadline(row.original),
        },
        {
            id: 'actions',
            header: () => <Box sx={{ textAlign: 'right' }}>Actions</Box>,
            cell: ({ row }) => {
                const r = row.original;
                return (
                    <Box sx={{ textAlign: 'right' }}>
                        <Tooltip title={r.gmail_thread_id ? 'Open in Gmail' : 'No thread ID'}>
                            <span>
                                {r.gmail_thread_id ? (
                                    <IconButton
                                        size="small"
                                        component="a"
                                        href={buildGmailUrl(r.gmail_thread_id)}
                                        target="_blank"
                                        rel="noopener"
                                    >
                                        <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                ) : (
                                    <IconButton size="small" disabled>
                                        <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </span>
                        </Tooltip>
                        <Tooltip title={(r.status === 'pending' || r.status === 'followed_up_waiting') ? 'Cancel follow-up' : 'Already closed'}>
                            <span>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setConfirmRow(r)}
                                    disabled={
                                        cancelingId === r.id ||
                                        !(r.status === 'pending' || r.status === 'followed_up_waiting')
                                    }
                                >
                                    <CancelIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                );
            },
        },
    // handleCancel is intentionally omitted from deps: it's invoked via
    // setConfirmRow(r) → Dialog onClick, which uses the parent's current
    // render-scope closure (not the one captured here), so we don't need
    // to rebuild columns when handleCancel changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [cancelingId]);

    const table = useReactTable({
        data: followups,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 10 } },
    });

    if (loading && followups.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <Loading />
            </Box>
        );
    }

    const { pageIndex, pageSize } = table.getState().pagination;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Follow-Ups</Typography>
                <Chip label={`${followups.length}`} color="primary" />
                <Box sx={{ flexGrow: 1 }} />
                <Select
                    size="small"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ minWidth: 180 }}
                >
                    {STATUS_FILTER_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </Select>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={loading}
                >
                    {loading ? 'Refreshing...' : 'Refresh'}
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{
                backgroundColor: colors.primary[600],
                boxShadow: 3,
            }}>
                <Table size="small">
                    <TableHead>
                        {table.getHeaderGroups().map(hg => (
                            <TableRow key={hg.id} sx={{ backgroundColor: colors.primary[700] }}>
                                {hg.headers.map(header => (
                                    <TableCell key={header.id} sx={{ fontWeight: 'bold' }}>
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {followups.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No follow-ups found.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            table.getRowModel().rows.map(row => (
                                <TableRow key={row.id} hover>
                                    {row.getVisibleCells().map(cell => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {followups.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                    <Typography variant="body2">Rows per page:</Typography>
                    <Select
                        size="small"
                        value={pageSize}
                        onChange={(e) => table.setPageSize(Number(e.target.value))}
                    >
                        {[10, 25, 50].map(n => (
                            <MenuItem key={n} value={n}>{n}</MenuItem>
                        ))}
                    </Select>
                    <Typography variant="body2">
                        {pageIndex * pageSize + 1}–{Math.min((pageIndex + 1) * pageSize, followups.length)} of {followups.length}
                    </Typography>
                    <Button
                        size="small"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        Prev
                    </Button>
                    <Button
                        size="small"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        Next
                    </Button>
                </Box>
            )}

            <Dialog
                open={Boolean(confirmRow)}
                onClose={() => setConfirmRow(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Stop tracking this follow-up?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmRow && (
                            <>
                                No automatic follow-up will be sent for{' '}
                                <strong>{confirmRow.recipient_email}</strong>
                                {confirmRow.subject ? <> regarding "<strong>{confirmRow.subject}</strong>"</> : null}.
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmRow(null)}>Keep tracking</Button>
                    <Button
                        onClick={() => {
                            const row = confirmRow;
                            setConfirmRow(null);
                            if (row) handleCancel(row);
                        }}
                        color="error"
                        variant="contained"
                    >
                        Stop tracking
                    </Button>
                </DialogActions>
            </Dialog>

            <Alert
                successAlertOpen={successOpen}
                setSuccessAlertOpen={setSuccessOpen}
                errorAlertOpen={errorOpen}
                setErrorAlertOpen={setErrorOpen}
                successMsg={successMsg}
                errorMsg={errorMsg}
                vert="bottom"
                horiz="left"
            />
        </Box>
    );
};

export default FollowUpDashboard;
